import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	forwardRef,
	HostListener,
	inject,
	Input,
	OnChanges,
	OnDestroy,
	Output,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";

const SELECT_CONTROL_ACCESSOR = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => SelectComponent),
	multi: true,
};

@Component({
	selector: "pv-select",
	templateUrl: "./select.component.html",
	providers: [SELECT_CONTROL_ACCESSOR],
	styleUrls: ["./select.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class SelectComponent implements ControlValueAccessor {
	private cdRef = inject(ChangeDetectorRef);
	private eRef = inject(ElementRef);

	@Input() icon: string;
	@Input() loading: boolean;
	@Input() placeholder: string;
	@Input() label: string;
	@Input() disabled = false;
	@Input() required = true;
	@Output() changeItem = new EventEmitter();
	@Input() help: string;
	@Input() missing = false;
	@Input() missingText: string;
	@Input() fitHeightToOptions: boolean;
	@Input() compare: (option: any, selected: any) => boolean;
	@Input() info: string;

	private onChange: (value: unknown) => void;
	private onTouch: () => void;

	options: SelectOptionComponent[] = [];
	selected: string;
	hasFocus = false;

	@HostListener("document:click", ["$event"])
	clickout(event) {
		if (!this.eRef.nativeElement.contains(event.target)) {
			this.hasFocus = false;
		}
	}

	selectOption(option: SelectOptionComponent) {
		if (this.disabled) {
			return;
		}
		this.selected = option.value;
		this.onChange(option.value);
		this.onTouch();
		this.hasFocus = false;
		this.changeItem.emit(option);
		this.cdRef.markForCheck();
	}

	getSelectedLabel() {
		if (this.compare) {
			const opt = this.options.find((value) => this.compare(value.value, this.selected));

			if (opt) {
				return opt.label;
			}
		}

		if (!this.selected) {
			return this.placeholder;
		}

		const option = this.options.find((value) => value.value === this.selected);

		if (!option) {
			return this.placeholder;
		}

		return option.label;
	}

	setFocus() {
		if (this.disabled) {
			return;
		}
		this.hasFocus = !this.hasFocus;
	}

	writeValue(selected: string): void {
		this.selected = selected;
		this.cdRef.markForCheck();
	}

	registerOnChange(fn: (value: unknown) => void) {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void) {
		this.onTouch = fn;
	}

	setDisabledState(isDisabled: boolean) {
		this.disabled = isDisabled;
		this.cdRef.detectChanges();
	}

	refresh() {
		this.cdRef.markForCheck();
	}
}

@Component({
	selector: "pv-select-option",
	template: '<li data-test="option"><ng-content /></li>',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class SelectOptionComponent implements OnChanges, OnDestroy {
	private cdRef = inject(ChangeDetectorRef);
	private select = inject(SelectComponent);

	@Input() value: string;
	@Input() label: string;
	@HostListener("click")
	onClick() {
		this.select.selectOption(this);
	}

	constructor() {
		this.select.options.push(this);
	}

	ngOnChanges() {
		this.select.refresh();
	}

	ngOnDestroy() {
		this.select.options = this.select.options.filter((option) => option !== this);
	}
}
